import * as yup from 'yup'
import merge from 'lodash/fp/merge'
// TODO: https://floatingapps.atlassian.net/browse/CP-10464?focusedCommentId=53672
import { defaultLocale } from '@chilipiper/locale'
import { ObjectTypeResponse } from '@chilipiper/api-type-def/src/response/crm'
import { Block } from '@chilipiper/api-type-def'
import { createValidation } from '@chilipiper/utils'
import {
  Field,
  FieldType,
  OptionsType,
} from '@chilipiper/meetings-components/src/guest-forms/types'
import { Form as FormModel } from '@chilipiper/meetings-components/src/guest-forms/models'
import { AvailabilityPayload, FieldProps, Prospect } from '../types'

export class GuestFormsCFAModel {
  static shouldUseSfFieldType = (
    field: Block,
    isCustomOption: boolean,
    isHotWorkspace?: boolean
  ) => {
    const hasOptions = field.options?.length > 0

    if (isHotWorkspace && !isCustomOption) {
      return true
    }
    if (isHotWorkspace && isCustomOption && !hasOptions) {
      return true
    }
    return false
  }
  static createFieldType = (
    useSfFields: boolean,
    field: any,
    sfField?: ObjectTypeResponse
  ): FieldType => {
    if (useSfFields) {
      return FormModel.mapHotFieldType(sfField?.types, sfField?.subType)
    }
    return field.fieldType
  }
  static createOptions = (useSfFields: boolean, field: any, sfField?: ObjectTypeResponse) => {
    if (useSfFields) {
      return sfField?.picklistValues.map(p => ({ label: p.label, value: p.value }))
    }
    return field.options.map((o: any) => ({ label: o, value: o }))
  }

  static createFields({
    contactFields,
    leadFields,
    form,
    locale,
    prospect,
    isHubspot,
    isHotWorkspace,
  }: AvailabilityPayload): FieldProps[] {
    const { fields } = FormModel.mapFormResult(form, isHubspot)
    const normalizedContactFields = normalizeSalesforceFields(contactFields)
    const normalizedLeadFields = normalizeSalesforceFields(leadFields)

    const result = fields.map(f => {
      const sfField =
        normalizedLeadFields.get(isHotWorkspace ? f.leadField : (f.pickListId ?? '')) ||
        normalizedContactFields.get(isHotWorkspace ? f.contactField : (f.pickListId ?? ''))

      const isCustomOption = FormModel.getOptionType(f) === OptionsType.Custom
      const useSfFieldType = this.shouldUseSfFieldType(f, isCustomOption, isHotWorkspace)
      const fieldType = this.createFieldType(useSfFieldType, f, sfField)
      const options = this.createOptions(!isCustomOption, f, sfField)

      const { label } = getLocalisedText(f, locale)
      const initialValue = getInitialValue(f, prospect) ?? ''
      return {
        label: label,
        hidden: f.hidden,
        name: f.fieldName,
        required: f.required,
        type: fieldType,
        options,
        initialValue: fieldType === 'Email' ? initialValue.replace(' ', '+') : initialValue,
      }
    })

    return result
  }

  static createValidationSchema({ form, locale }: Pick<AvailabilityPayload, 'form' | 'locale'>) {
    const { fields } = FormModel.mapFormResult(form)
    const formBlueprint = fields.map(field => {
      const { error } = getLocalisedText(field, locale)
      const buildSchema = (field: Field, schema: yup.StringSchema) => {
        const base = schema.label(field.label)
        return field.required ? base.trim().required(error) : base.notRequired()
      }

      switch (field.fieldType) {
        case FieldType.Email:
          return {
            [field.fieldName]: buildSchema(field, yup.string().email()),
          }
        case FieldType.Phone:
          return {
            [field.fieldName]: field.restricted
              ? buildSchema(
                  field,
                  yup
                    .string()
                    .matches(/^\+?\d{8,}$/, {
                      message: error,
                    })
                    .default('')
                )
              : buildSchema(field, yup.string()),
          }
        case FieldType.SingleLine:
          return {
            [field.fieldName]: buildSchema(field, yup.string()),
          }
        case FieldType.Multiline:
          return {
            [field.fieldName]: buildSchema(field, yup.string()),
          }
        case FieldType.MultipleSelection:
          return {
            [field.fieldName]: buildSchema(field, yup.string()),
          }
        case FieldType.SingleSelection:
          return {
            [field.fieldName]: buildSchema(field, yup.string()),
          }
        case FieldType.Select:
          return {
            [field.fieldName]: buildSchema(field, yup.string()),
          }
        default:
          return {
            [field.fieldName]: buildSchema(field, yup.string()),
          }
      }
    })

    const schemaShape = formBlueprint.reduce((schema, rule) => merge(rule, schema), {})

    const schema = yup.object().shape({
      ...schemaShape,
    })

    return createValidation(schema)
  }
}

const normalizeSalesforceFields = (fields: ObjectTypeResponse[]) => {
  return fields.reduce(
    (acc, field) => acc.set(field.name, field),
    new Map<string, ObjectTypeResponse>()
  )
}

const getLocalisedText = (field: Field, locale: string) => {
  const locales = field.locales || {}

  // TODO: https://floatingapps.atlassian.net/browse/CP-10464?focusedCommentId=53672'
  const selectedLocale = locales[locale] || locales[defaultLocale] || {}

  const { label, error }: { error: string; label: string } = selectedLocale

  return { label: label || field.label, error }
}

const getInitialValue = (field: Field, prospect: Prospect = {}) => {
  return (
    new URLSearchParams(window.location.search).get(field.fieldName) ||
    prospect[field.fieldName] ||
    field.defaultValue
  )
}
